import { EventCodes } from '../../../App/model/event-codes';

ActionExecutorService.$inject = [
	'$q',
	'mx.shell.NotificationService',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.HelpService',
	'mx.SolutionBuilderAgent.Http',
	'mx.shell.DownloadHelpService',
	'mx.shell.EventService',
	'mx.shell.SidePanelService',
	'mx.SolutionBuilderAgent.DialogOpenerService',
	'mx.shell.InjectorService',
	'mx.shell.ContextService',
	'mx.shell.SideLayout',
	'mx.shell.ActionProviderService',
	'mx.shell.ActionManagerService',
	'mx.SolutionBuilderAgent.ActionCoRuService',
	'mx.SolutionBuilderAgent.SchemaService',
];

export function ActionExecutorService(
	$q,
	notificationService,
	i18n,
	helpService,
	http,
	downloadHelpService,
	eventService,
	sidePanelService,
	dialogOpenerService,
	injectorService,
	contextService,
	sideLayoutService,
	actionProviderService,
	actionManagerService,
	actionCoRuService,
	schemaService
) {
	return {
		execute: execute,
		executeEditAction: executeEditAction,
		executeCreateAction: executeCreateAction,
	};

	function processResponse(action, result, objects) {
		if (result.response.headers('sys-force-app-reload') === 'true') {
			window.forceReload();
		}

		const contentDisposition = result.response.headers('Content-Disposition');

		if (contentDisposition && contentDisposition.includes('attachment') && !downloadHelpService.downloadContent(result.response, result.data)) {
			throw new Error(i18n.get('solutionBuilderAgent.actions.operation-action.download-error'));
		}

		if (result.refresh) {
			const objectIds = (result.operationObjects || []).reduce((ids, entry) => ids.concat((entry && entry.ObjectIds) || []), []);

			eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.SearchPageDataChanged);

			if (objectIds.length) {
				if (result.method === mx.shell.HttpMethod.Delete) {
					eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ObjectDataDeleted, { affectedObjectIds: objectIds });
				} else {
					if (!action.serviceOperation.OpenDialogOnSuccess) {
						eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ObjectDataChanged, {
							affectedObjectIds: objectIds,
						});
					}
				}
			}
		}
		notificationService.success(
			i18n.getFormatted('solutionBuilderAgent.actions.operation-action.success', action.title),
			i18n.getFormatted('solutionBuilderAgent.actions.operation-action.ARIA-success', action.title)
		);

		if (action.serviceOperation && action.serviceOperation.OpenDialogOnSuccess && Array.isArray(objects)) {
			objects.forEach((item) => {
				dialogOpenerService.openEditDialog(item, true);
			});
		}
	}

	function filterObjects(action, selectedObjects) {
		let objects = typeof selectedObjects === 'undefined' ? contextService.selectedObjects : selectedObjects;

		return objects.filter((object) => {
			if (action.availableTypes && action.availableTypes.length) {
				const isTypeAvailable = action.availableTypes.includes(object._type);
				if (action.typesFilter === mx.shell.ActionFilterType.Specified) {
					return isTypeAvailable;
				} else if (action.typesFilter === mx.shell.ActionFilterType.ExceptSpecified) {
					return !isTypeAvailable;
				}
			}
			return true;
		});
	}

	function trackTelemetry(action, objects) {
		const types = objects.map(({ _type }) => _type).filter((value, index, array) => array.indexOf(value) === index);

		Promise.all(types.map((type) => schemaService.getType(type))).then((typesInfo) => {
			eventService.dispatchEvent(EventCodes.TrackTelemetry, {
				eventName: `Action clicked`,
				sender: action.id,
				senderTitle: action.name,

				contextId: objects.map(({ _id }) => _id),
				contextTitle: objects.map(({ _name, _displayName }) => _displayName || _name),
				contextTypeId: typesInfo.map((typeInfo) => typeInfo?.Id),
				contextTypeTitle: typesInfo.map((typeInfo) => typeInfo?.InternalName),
			});
		});
	}

	/**
	 * @ngdoc method
	 * @name mx.shell.ActionExecutorService#execute
	 * @async
	 *
	 * @description
	 * Runs the {@ref mx.shell.Action} for the specifies objects.
	 *
	 * @param {mx.shell.Action} action - Specifies the {@ref mx.shell.Action} definition.
	 * @param {mx.shell.Object[]} [selectedObjects] - Specifies the list of {@ref mx.shell.Object objects} which affected
	 * @param {mx.shell.View} [parentView] - Specifies the parent view {@ref mx.shell.View}
	 * @param {Object} [presetFields] - Specifies the preset fields
	 * by the Action execution. If not provided mx.shell.ContextService.selectedObjects is used
	 */
	async function execute(action, selectedObjects, parentView, presetFields) {
		if (!action) {
			throw new Error(i18n.get('shell.errors.action_not_provided'));
		}

		const objects = filterObjects(action, selectedObjects);
		const actionAvailabilityInfo = await actionManagerService.checkAvailability(action, objects);

		if (!actionAvailabilityInfo.isAvailable) {
			const errorMessage = i18n.get('solutionBuilderAgent.actions.actionUnavailable');
			notificationService.error(errorMessage);
			throw new Error(errorMessage);
		}

		trackTelemetry(action, objects);

		if (action.serviceOperation) {
			let confirmed = !!action.ignoreConfirmationForServiceOperation;
			if (!confirmed) {
				const dialogConfig = {
					id: 'ActionOnExecuteServiceOperation-' + action.serviceOperation.ServiceOperation,
					title: action.title,
					message: action.serviceOperation.ConfirmationMessage || i18n.get('solutionBuilderAgent.actions.operation-action.confirmation-message'),
					actions: [
						{
							id: 'no',
							notForSave: true,
							title: i18n.get('solutionBuilderAgent.actions.operation-action.no'),
						},
						{
							id: 'yes',
							isConfirmButton: true,
							title: i18n.get('solutionBuilderAgent.actions.operation-action.yes'),
						},
					],
					buttonsInActionZone: true,
				};

				const dialogAnswer = await sidePanelService.showConfirmationDialog(dialogConfig);

				if (dialogAnswer === 'yes') {
					eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ActionConfirmDialogConfirmed);
					confirmed = true;
				} else {
					eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.ActionConfirmDialogCanceled);
				}
			}

			if (confirmed) {
				try {
					const operationResult = await executeOperation(action, objects);

					if (operationResult && operationResult.response) {
						processResponse(action, operationResult, objects);
						eventService.dispatchEvent(mx.shell.EventCodes.ActionExecuted, { action, parentView, finishPromise: $q.when() });
					}

					if (action.reloadPageOnSuccess) {
						window.forceReload();
					}
				} catch (error) {
					notificationService.error(error);
				}
			}
		} else {
			if (typeof action.viewFn === 'function') {
				action.view = await action.viewFn(objects, presetFields);
			}

			const controller = await injectorService.controller(action);
			await controllerExecute(action, controller, objects, parentView, presetFields);
		}

		//we call that method even wizard or action doesn't contain submit service operation.
		//we currently have fuzzy logic for handle CoRus for Action without submit operation.
		const result = await actionCoRuService.commit(action.id, actionAvailabilityInfo.cacheId);

		return result;
	}

	async function controllerExecute(action, controller, objects, parentView, presetFields) {
		let data = null;
		let view = action.view;
		let viewPromise;

		if (controller && controller.execute && typeof controller.execute === 'function') {
			data = await controller.execute(objects, action, presetFields, parentView);
		} else if (controller && controller.getView && typeof controller.getView === 'function') {
			view = controller.getView(objects);
		}

		if (view) {
			if (action.useSameView) {
				const currentView = sidePanelService.getCurrentView();

				if (currentView && currentView.id !== 'selection') {
					view.name = currentView.name;
					view.displayName = currentView.displayName;
					view.headerTitle = currentView.headerTitle;
					view.headerIcon = currentView.headerIcon;
					view.id = currentView.id;
				}
			} else {
				if (view.isWizard) {
					view.headerIcon = action.icon;
				}

				view.id = (view.isWizard ? action.name + ':' : '') + objects.map((o) => o._id).join(';');
			}

			view.context = objects[0];

			view.locals = angular.extend(view.locals || {}, {
				selectedObjects: objects,
				context: objects[0],
				view: view,
				isLazy: action.isLazy,
			});

			view.startedFromAction = action;
			view.actionContext = objects;

			viewPromise = sidePanelService.addView(view, parentView);

			if (action.openInFullScreen) {
				sideLayoutService.switchFullScreenMode(true);
			}
		}

		if (data && data.view) {
			view = data.view;
			view.startedFromAction = action;
			view.actionContext = objects;
			viewPromise = view.finishDefer && view.finishDefer.promise;
		}

		eventService.dispatchEvent(mx.shell.EventCodes.ActionExecuted, { action, view, parentView, finishPromise: viewPromise });
		return viewPromise;
	}

	function getObjectIds(selectedObjects) {
		return selectedObjects ? selectedObjects.map((obj) => obj._id || obj.ID || obj['Sys-ObjectId']).filter((objectId) => objectId !== null && objectId !== undefined) : [];
	}

	function executeOperation(action, selectedObjects) {
		return helpService.getOperationInfo(action.serviceOperation.ServiceOperation).then((operationInfo) => {
			if (typeof operationInfo === 'object' && operationInfo.RelativePath) {
				const method = mx.shell.HttpMethod.find(operationInfo.HttpMethod);
				let operationObjects, promise;

				if (
					typeof operationInfo.RequestContract === 'object' &&
					typeof operationInfo.RequestContract.UsedTypes === 'object' &&
					Object.keys(operationInfo.RequestContract.UsedTypes).length > 0
				) {
					operationObjects = getOperationObjects(action, selectedObjects);
				}

				let refresh = true;
				const objectIds = getObjectIds(selectedObjects);

				const config = actionCoRuService.getActionContextHeader(action.id, objectIds) || {};
				config.eventHandlers = {
					loadstart: function (event) {
						event.target.addEventListener('readystatechange', function (event) {
							if (event.target.readyState === 2) {
								const headers = event.target.getAllResponseHeaders();
								const isFileDownload = headers.toLowerCase().indexOf('content-disposition') !== -1;

								if (isFileDownload) {
									event.target.responseType = 'arraybuffer';
									refresh = false;
								}
							}
						});
					},
				};

				let url = operationInfo.RelativePath;
				if (url.indexOf('{actionId}') > 0) {
					url = url.replace('{actionId}', action.id);
				}

				switch (method) {
					case mx.shell.HttpMethod.Get:
						promise = http.get(url + (operationObjects ? '?Objects=' + JSON.stringify(operationObjects) : ''), '', config, false, true);
						refresh = false;
						break;
					case mx.shell.HttpMethod.Post:
						promise = http.post(url, operationObjects ? { Objects: operationObjects } : {}, config, {}, false, true);
						break;
					case mx.shell.HttpMethod.Put:
						promise = http.put(url, operationObjects ? { Objects: operationObjects } : {}, config, {}, false, true);
						break;
					case mx.shell.HttpMethod.Delete:
						promise = http.remove(url, operationObjects ? { Objects: operationObjects } : {}, {}, {}, false, true);
						break;
					default:
						break;
				}

				if (promise) {
					return promise.then(function (response) {
						return {
							data: response.data,
							response: response,
							operationObjects: operationObjects,
							method: method,
							refresh: refresh,
						};
					});
				}
			}
		});
	}

	function getOperationObjects(action, selectedObjects) {
		const objects = filterObjects(action, selectedObjects);

		const operationObjects = [];

		objects.forEach(function (newObject) {
			if (
				!operationObjects.some((addedObject) => {
					if (addedObject.TypeName === newObject._type) {
						addedObject.ObjectIds.push(newObject._id);
						return true;
					}
				})
			) {
				operationObjects.push({
					ObjectIds: [newObject._id],
					TypeName: newObject._type,
				});
			}
		});

		return operationObjects;
	}

	function executeCreateAction(type, parentView, presetFields) {
		return actionManagerService.getCreateActions(type).then(function (createActions) {
			const createAction = createActions[0];
			if (!createAction) {
				return $q.reject('create action was not found');
			}
			return execute(createAction, [], parentView, presetFields);
		});
	}

	function executeEditAction(object, parentView, presetFields) {
		return actionManagerService.getEditActions(object).then(function (editActions) {
			const editAction = editActions[0];
			if (!editAction) {
				return $q.reject('edit action was not found');
			}
			return execute(editAction, [object], parentView, presetFields);
		});
	}
}
